import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Button,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Stack,
} from "@mui/material";
import {
  setTitle,
  setAbout,
  setGender,
  setPublish,
  setLinks,
} from "../../../store/setProfile";
import { useDispatch, useSelector } from "react-redux";
import UploadAvatar from "../../Profile/Components/UploadAvatar";
import WebcamCapture from "../../Profile/Components/WebcamCapture";
import SocialLinkInput from "../../../Components/SocialLinkInput";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { PhotoCamera } from "@mui/icons-material";
import { toast } from "react-toastify";
import { checkAndRequestCameraPermission } from "../../../utils/video-recorder";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";

const maxWords = 300;
const maxTitleLength = 50;

const linkNames = [
  { key: "facebook", value: "FaceBook" },
  { key: "instagram", value: "Instagram" },
  { key: "x", value: "X" },
  { key: "website", value: "Website" },
  { key: "linkedin", value: "Linkedin" },
];

const modules = {
  toolbar: [[{ list: "bullet" }]],
};

const formats = ["list", "bullet"];

const stripHtml = (html: string) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || "";
};

const countWords = (text: string) => {
  const cleanedText = stripHtml(text).trim();
  return cleanedText.split(/\s+/).filter(Boolean).length;
};

export default function TutorProfile({ onDirtyChange, onSave }: any) {
  const dispatch = useDispatch();
  const { title, about, gender, publish, links, avatar } = useSelector(
    (state: any) => state.profile.information
  );
  const [newLink, setNewLink] = useState<any>(null);
  const [linkError, setLinkError] = useState(false);
  const [isSocialPlatformSelected, setIsSocialPlatformSelected] =
    useState<boolean | null>(null);
  const [showOutlineDeleteIcon, setShowOutlineDeleteIcon] = useState(true);
  const [showOutlineCheckIcon, setShowOutlineCheckIcon] = useState(true);
  const [openCaptureDialog, setOpenCaptureDialog] = useState(false);

  useEffect(() => {
    onDirtyChange?.(false);
  }, []);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    if (newTitle.length <= maxTitleLength) {
      dispatch(setTitle(newTitle));
      onDirtyChange?.(true);
    }
  };

  const handleAboutChange = (value: string) => {
    dispatch(setAbout(value));
    onDirtyChange?.(true);
  };

  const handleGenderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setGender(parseInt(e.target.value)));
    onDirtyChange?.(true);
  };

  const handlePublishChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPublish(parseInt(e.target.value)));
    onDirtyChange?.(true);
  };

  const validateLink = (linkName: string, linkValue: string) => {
    let isValid = false;
    switch (linkName) {
      case "facebook":
        isValid = /(?:https?:\/\/)?(?:www\.)?(?:facebook\.com)\/(?:[^\/?]+\/?|groups\/[^\/?]+\/?|pages\/[^\/?]+\/?)/gm.test(
          linkValue
        );
        break;
      case "instagram":
        isValid = /(?:https?:\/\/)?(?:www\.)?(?:instagram\.com|instagr\.am)\/(?:[^\/?]+\/?|p\/[^\/?]+\/?|tv\/[^\/?]+\/?)/gm.test(
          linkValue
        );
        break;
      case "website":
        isValid = /^(?:https?:\/\/)?(?:www\.)[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?$/gm.test(
          linkValue
        );
        break;
      case "linkedin":
        isValid = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$.*/i.test(
          linkValue
        );
        break;
      case "x":
        const isValidTwitter =
          /^(https?:\/\/)?(www\.)?twitter\.com\/[a-zA-Z0-9_]+\/?$.*/i.test(
            linkValue
          );
        const isValidX =
          /^(https?:\/\/)?(www\.)?x\.com\/[a-zA-Z0-9_]+\/?$.*/i.test(linkValue);
        isValid = isValidTwitter || isValidX;
        break;
      default:
        break;
    }
    return isValid;
  };

  const getSocialPlatformPlaceholder = (linkName: string) => {
    const placeholders: Record<string, string> = {
      facebook: "e.g. www.facebook.com/example.user",
      instagram: "e.g. www.instagram.com/example.user",
      website: "e.g. www.example.co.uk",
      linkedin: "e.g. www.linkedin.com/in/example.user",
      x: "e.g. www.twitter.com/example.user",
    };
    return placeholders[linkName] || "Please add your selected profile url";
  };

  const handleSaveLink = () => {
    if (!newLink?.linkName) {
      setIsSocialPlatformSelected(false);
      return;
    }
    const isValid = validateLink(newLink.linkName, newLink.linkValue);
    if (!newLink.linkValue || !isValid) {
      setLinkError(true);
      return;
    }
    if (isValid && !linkError && isSocialPlatformSelected) {
      dispatch(setLinks({ [newLink.linkName]: newLink.linkValue }));
      setNewLink(null);
      setLinkError(false);
      setIsSocialPlatformSelected(null);
      onDirtyChange?.(true);
    }
  };

  const handleSocialLinkInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewLink({ ...newLink, linkValue: e.target.value });
    setLinkError(false);
  };

  const addNewLink = () => {
    if (newLink) return;
    setLinkError(false);
    setNewLink({
      linkName: "",
      linkValue: "",
    });
  };

  const deleteNewLink = () => {
    setNewLink(null);
    setLinkError(false);
    setIsSocialPlatformSelected(true);
  };

  const handleOpenImageCapture = async () => {
    if (!(await checkAndRequestCameraPermission())) {
      toast.error(
        "Camera permission denied. Please allow camera permission to capture photo."
      );
      return;
    }
    setOpenCaptureDialog(true);
  };

  const handleCloseImageCapture = () => {
    setOpenCaptureDialog(false);
  };

  const handleOpenCaptureDialog = handleOpenImageCapture;

  (TutorProfile as any).valid =
    title &&
    gender !== -1 &&
    about &&
    about !== "<p><br></p>" &&
    avatar &&
    countWords(about) <= maxWords;
  (TutorProfile as any).save = async () => {
    if (countWords(about) > maxWords) {
      toast.error("You have exceeded the maximum words limit.");
      return;
    }
    onDirtyChange?.(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
        Create your tutor profile
      </Typography>
      <Typography variant="body2" sx={{ mb: 4, color: "#6b7280" }}>
        (Some sections require completion)
      </Typography>

      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
          mb: 3,
        }}
      >
        <CardContent>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel
              id="title-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              <span style={{ color: "red" }}>*</span> Write a title of your
              profile
            </FormLabel>
            <TextField
              fullWidth
              value={title || ""}
              onChange={handleTitleChange}
              placeholder="e.g. Experienced Maths Tutor"
              error={title && title.length >= maxTitleLength}
              helperText={
                title && title.length >= maxTitleLength
                  ? "You have reached the limit of 50 characters"
                  : ""
              }
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel
              id="avatar-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              <span style={{ color: "red" }}>*</span> Upload a professional
              profile picture of yourself
              <Typography
                component="span"
                sx={{ color: "gray", fontSize: "0.875rem", ml: 1 }}
              >
                (Logo/Illustration not allowed)
              </Typography>
            </FormLabel>
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <UploadAvatar showCropper={true} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenCaptureDialog}
                endIcon={<PhotoCamera />}
              >
                Capture Photo
              </Button>
            </Box>
            {openCaptureDialog && (
              <WebcamCapture
                open={openCaptureDialog}
                handleClose={handleCloseImageCapture}
              />
            )}
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel
              id="about-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              <span style={{ color: "red" }}>*</span> About you
            </FormLabel>
            <ReactQuill
              theme="snow"
              value={about || ""}
              modules={modules}
              formats={formats}
              placeholder="Tell us about yourself e.g. experience as a tutor, teaching style or approach. Limit: 300 words."
              onChange={handleAboutChange}
              style={{ marginBottom: "1rem" }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color:
                    countWords(about || "") <= maxWords ? "green" : "#da1e28",
                  mr: 1,
                }}
              >
                {countWords(about || "") <= maxWords
                  ? `${maxWords - countWords(about || "")} words left.`
                  : `Limit exceeded by ${
                      countWords(about || "") - maxWords
                    } words.`}
              </Typography>
              {countWords(about || "") <= maxWords ? (
                <CheckCircleOutlineIcon
                  sx={{ color: "green", fontSize: "1.25rem" }}
                />
              ) : (
                <CheckCircleRoundedIcon
                  sx={{ color: "#da1e28", fontSize: "1.25rem" }}
                />
              )}
            </Box>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel
              id="gender-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              <span style={{ color: "red" }}>*</span> Gender
            </FormLabel>
            <RadioGroup
              row
              value={gender !== undefined ? String(gender) : "-1"}
              onChange={handleGenderChange}
            >
              <FormControlLabel
                value="0"
                control={<Radio />}
                label="Male"
              />
              <FormControlLabel
                value="1"
                control={<Radio />}
                label="Female"
              />
            </RadioGroup>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel
              id="publish-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              Allow my profile to be published on search engines e.g. google,
              safari{" "}
              <Typography
                component="span"
                sx={{ color: "#929292", fontSize: "0.875rem" }}
              >
                (Optional)
              </Typography>
            </FormLabel>
            <RadioGroup
              row
              value={publish !== undefined ? String(publish) : "0"}
              onChange={handlePublishChange}
            >
              <FormControlLabel value="0" control={<Radio />} label="Yes" />
              <FormControlLabel value="1" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          <FormControl fullWidth>
            <FormLabel
              id="social-links-label"
              sx={{ mb: 1, fontWeight: 600, fontSize: "1rem" }}
            >
              Social Links{" "}
              <Typography
                component="span"
                sx={{ color: "#929292", fontSize: "0.875rem" }}
              >
                (Optional)
              </Typography>
            </FormLabel>
            <Typography variant="body2" sx={{ mb: 2, color: "#6b7280" }}>
              Link your social media. This helps parents, students, and search
              engines discover you.
            </Typography>
            {Object.keys(links || {})
              .filter((key) => links[key])
              .map((key, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <SocialLinkInput linkName={key} linkValue={links[key]} />
                </Box>
              ))}
            {newLink && (
              <Box sx={{ mt: 2, p: 2, border: "1px solid #e9e9ec", borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel id="social-platform-label">
                        Social Platform
                      </InputLabel>
                      <Select
                        labelId="social-platform-label"
                        value={newLink.linkName}
                        onChange={(e) => {
                          setNewLink({
                            linkName: e.target.value,
                            linkValue: "",
                          });
                          setLinkError(false);
                          setIsSocialPlatformSelected(true);
                        }}
                        label="Social Platform"
                        error={isSocialPlatformSelected === false}
                      >
                        {linkNames
                          .filter((linkName) => !links?.[linkName.key])
                          .map((linkName, index) => (
                            <MenuItem key={index} value={linkName.key}>
                              {linkName.value}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      value={newLink.linkValue || ""}
                      placeholder={
                        newLink.linkName
                          ? getSocialPlatformPlaceholder(newLink.linkName)
                          : "Select a platform first"
                      }
                      onChange={handleSocialLinkInputChange}
                      disabled={!isSocialPlatformSelected}
                      error={!!linkError}
                      helperText={
                        linkError
                          ? `Please enter a valid URL for ${newLink.linkName}`
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        onMouseEnter={() => setShowOutlineCheckIcon(false)}
                        onMouseLeave={() => setShowOutlineCheckIcon(true)}
                        onClick={handleSaveLink}
                        color="success"
                        disabled={!newLink.linkValue}
                      >
                        {showOutlineCheckIcon ? (
                          <CheckCircleOutlineIcon />
                        ) : (
                          <CheckCircleRoundedIcon />
                        )}
                      </IconButton>
                      <IconButton
                        onMouseEnter={() => setShowOutlineDeleteIcon(false)}
                        onMouseLeave={() => setShowOutlineDeleteIcon(true)}
                        onClick={deleteNewLink}
                        color="error"
                      >
                        {showOutlineDeleteIcon ? (
                          <CancelOutlinedIcon />
                        ) : (
                          <CancelRoundedIcon />
                        )}
                      </IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}
            <Button
              variant="contained"
              onClick={addNewLink}
              disabled={
                Object.keys(links || {}).filter((key) => !links[key]).length ===
                  0 || !!newLink
              }
              sx={{ mt: 2 }}
            >
              Add Link
            </Button>
          </FormControl>
        </CardContent>
      </Card>
    </Box>
  );
}
